import type {
  Answers,
  Axis,
  BoardAnswer,
  AxisAnswer,
  Invite,
  LanguageAnswer,
  LanguageSheet,
  Survey,
} from '@cf/shared'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null
    throw new ApiError(body?.message ?? `błąd ${response.status}`, response.status)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/* -------------------------------------------------------------- reviewer */

export const reviewApi = {
  invite: (token: string) => request<Invite>(`/api/r/${token}`),
  report: (token: string) => request<{ axes: Axis[] }>(`/api/r/${token}/report`),
  answers: (token: string) => request<Answers>(`/api/r/${token}/answers`),
  saveBoard: (token: string, boardId: string, answer: BoardAnswer) =>
    request<{ ok: true }>(`/api/r/${token}/boards/${encodeURIComponent(boardId)}`, {
      method: 'PUT',
      body: JSON.stringify(answer),
    }),
  saveAxis: (token: string, axisId: string, answer: AxisAnswer) =>
    request<{ ok: true }>(`/api/r/${token}/axes/${encodeURIComponent(axisId)}`, {
      method: 'PUT',
      body: JSON.stringify(answer),
    }),
  cursor: (token: string, cursor: number) =>
    request<{ ok: true }>(`/api/r/${token}/cursor`, {
      method: 'PUT',
      body: JSON.stringify({ cursor }),
    }),
  submit: (token: string, survey: Survey) =>
    request<{ ok: true }>(`/api/r/${token}/submit`, {
      method: 'POST',
      body: JSON.stringify(survey),
    }),
}

/* -------------------------------------------------------------- language */

export type LanguagePayload = {
  token: string
  nick: string
  displayName: string | null
  sheet: LanguageSheet
  totalItems: number
  answers: Record<
    string,
    { choice: string | null; goodChoice: string | null; note: string | null }
  >
  general: string | null
  submittedAt: string | null
}

export const languageApi = {
  payload: (token: string) => request<LanguagePayload>(`/api/l/${token}`),
  save: (token: string, itemId: string, answer: LanguageAnswer) =>
    request<{ ok: true }>(`/api/l/${token}/items/${encodeURIComponent(itemId)}`, {
      method: 'PUT',
      body: JSON.stringify(answer),
    }),
  submit: (token: string, general: string | null) =>
    request<{ ok: true }>(`/api/l/${token}/submit`, {
      method: 'POST',
      body: JSON.stringify({ general }),
    }),
}

/* ----------------------------------------------------------------- admin */

export type DatasetSummary = {
  id: string
  slug: string
  version: number
  label: string
  playerName: string
  revision: string
  layout: string
  band: string
  games: number
  decisions: number
  totalBoards: number
  status: string
  importedAt: string
  axes: number
}

export type ReviewerRow = {
  id: string
  nick: string
}

export type InviteRow = {
  id: string
  token: string
  createdAt: string
  openedAt: string | null
  lastSeenAt: string | null
  submittedAt: string | null
  revokedAt: string | null
  cursor: number
  nick: string
  reviewerId: string
  datasetId: string
  datasetSlug: string
  datasetVersion: number
  datasetLabel: string
  totalBoards: number
  answered: number
}

export type FeedbackRow = {
  board: Axis['boards'][number] & { seq: number }
  axisKey: string
  axisOrdinal: number
  axisTitle: string
  counts: { trafny: number; czesciowo: number; nietrafny: number; answers: number }
  variants: Array<{
    variant: string
    /** Who wrote the wording (e.g. "agent", "deepseek"); admin-only. */
    generator: string | null
    trafny: number
    czesciowo: number
    nietrafny: number
    preferred: number
  }>
  verdict: 'keep' | 'rewrite' | 'drop' | null
  verdictNote: string | null
  suggestedRating: number | null
  responses: Array<{
    nick: string
    title: string | null
    updatedAt: string
    answer: BoardAnswer
  }>
}

export const adminApi = {
  session: () => request<{ ok: true }>('/api/admin/session'),
  login: (password: string) =>
    request<{ ok: true }>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),
  datasets: () => request<DatasetSummary[]>('/api/admin/datasets'),
  importDataset: (document: unknown) =>
    request<{ slug: string; version: number; boards: number; reused: boolean }>(
      '/api/admin/datasets',
      { method: 'POST', body: JSON.stringify(document) },
    ),
  renameDataset: (id: string, input: { label?: string; revision?: string }) =>
    request<DatasetSummary>(`/api/admin/datasets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  reviewers: () => request<ReviewerRow[]>('/api/admin/reviewers'),
  createReviewer: (nick: string) =>
    request<ReviewerRow>('/api/admin/reviewers', {
      method: 'POST',
      body: JSON.stringify({ nick }),
    }),
  deleteReviewer: (id: string, force: boolean) =>
    request<{ ok: true; deletedAnswers: number }>(
      `/api/admin/reviewers/${id}?force=${force}`,
      { method: 'DELETE' },
    ),
  invites: () => request<InviteRow[]>('/api/admin/invites'),
  createInvite: (datasetSlug: string, reviewerId: string) =>
    request<{ token: string; reused: boolean }>('/api/admin/invites', {
      method: 'POST',
      body: JSON.stringify({ datasetSlug, reviewerId }),
    }),
  revokeInvite: (id: string) =>
    request<{ ok: true }>(`/api/admin/invites/${id}/revoke`, { method: 'POST' }),
  reopenInvite: (id: string) =>
    request<{ ok: true }>(`/api/admin/invites/${id}/reopen`, { method: 'POST' }),
  feedback: (datasetId: string, filter: string) =>
    request<FeedbackRow[]>(
      `/api/admin/feedback/${encodeURIComponent(datasetId)}?filter=${filter}`,
    ),
  submissions: (datasetId: string) =>
    request<
      Array<{
        nick: string
        submittedAt: string
        exercises: string | null
        exclusions: string | null
        general: string | null
      }>
    >(`/api/admin/submissions/${encodeURIComponent(datasetId)}`),
  setVerdict: (input: {
    boardId: string
    verdict: 'keep' | 'rewrite' | 'drop'
    note?: string | null
    rating?: number
  }) =>
    request<{ ok: true }>('/api/admin/verdicts', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  exportReview: (datasetId: string) =>
    request<Record<string, unknown>>(
      `/api/admin/export/review/${encodeURIComponent(datasetId)}`,
    ),
  exportFeedback: (datasetId: string) =>
    request<Record<string, unknown>>(
      `/api/admin/export/feedback/${encodeURIComponent(datasetId)}`,
    ),
}
